// src/components/public/PublicLayout.js
// Shared frame for the public (logged-out) pages: header, footer, colour theme.
import React, { createContext, useContext, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './publicTheme.css';

// Colour themes (see publicTheme.css). Each sets the whole look of every
// public page: background, accent, buttons, panels and cards.
export const PUBLIC_THEMES = {
  bright: { name: 'Navy, lighter' },
  navy: { name: 'Navy (old)' },
};
// Homepage hero layout: 'panel' is a full-height side panel, 'float' a card
// over the photo.
export const HERO_LAYOUTS = {
  panel: { name: 'Side panel' },
  float: { name: 'On photo' },
};
// Chosen by the owner: the deeper navy, and the sign-in card on a full-width photo.
const DEFAULT_THEME = 'navy';
const DEFAULT_LAYOUT = 'float';
const STORAGE_KEY = 'loadmemo.publicTheme';
const LAYOUT_STORAGE_KEY = 'loadmemo.publicHeroLayout';
// The picker is for choosing a theme during development; it never ships.
const SHOW_THEME_SWITCHER = process.env.NODE_ENV !== 'production';

const readInitial = (param, storageKey, options, fallback) => {
  if (!SHOW_THEME_SWITCHER) return fallback;
  const fromUrl = new URLSearchParams(window.location.search).get(param);
  if (options[fromUrl]) return fromUrl;
  try {
    const saved = window.localStorage.getItem(storageKey);
    if (options[saved]) return saved;
  } catch (e) { /* storage unavailable */ }
  return fallback;
};

const save = (storageKey, value) => {
  try { window.localStorage.setItem(storageKey, value); } catch (e) { /* ignore */ }
};

const ThemeContext = createContext({ key: DEFAULT_THEME, layout: DEFAULT_LAYOUT });
export const usePublicTheme = () => useContext(ThemeContext);

// Wrap every public page in this. It supplies the page background, header,
// footer and the active theme.
export const PublicPage = ({ children, className = '' }) => {
  const [themeKey, setThemeKey] = useState(() => readInitial('theme', STORAGE_KEY, PUBLIC_THEMES, DEFAULT_THEME));
  const [layout, setLayout] = useState(() => readInitial('hero', LAYOUT_STORAGE_KEY, HERO_LAYOUTS, DEFAULT_LAYOUT));

  const pickTheme = (key) => { setThemeKey(key); save(STORAGE_KEY, key); };
  const pickLayout = (key) => { setLayout(key); save(LAYOUT_STORAGE_KEY, key); };

  return (
    <ThemeContext.Provider value={{ key: themeKey, layout }}>
      <div className={`pub-${themeKey} min-h-screen flex flex-col pub-page font-sans text-white ${className}`}>
        <PublicHeader />
        <main className="flex-1">{children}</main>
        <PublicFooter />
        {SHOW_THEME_SWITCHER && !new URLSearchParams(window.location.search).has('shot') && (
          <ThemeSwitcher theme={themeKey} onTheme={pickTheme} layout={layout} onLayout={pickLayout} />
        )}
      </div>
    </ThemeContext.Provider>
  );
};

const SwitchGroup = ({ label, options, value, onChange, numbered }) => (
  <div className="flex flex-wrap items-center gap-1">
    <span className="px-2 text-slate-400">{label}</span>
    {Object.entries(options).map(([key, opt], i) => (
      <button
        key={key}
        onClick={() => onChange(key)}
        className={`px-3 py-1.5 rounded-lg transition ${value === key ? 'bg-white text-black' : 'text-slate-200 hover:bg-white/10'}`}
      >
        {numbered ? `${i + 1}. ` : ''}{opt.name}
      </button>
    ))}
  </div>
);

const ThemeSwitcher = ({ theme, onTheme, layout, onLayout }) => (
  <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] w-max max-w-[calc(100vw-2rem)] flex flex-wrap items-center justify-center gap-x-4 gap-y-1 p-1.5 rounded-xl bg-black/85 backdrop-blur border border-white/20 shadow-2xl text-sm">
    <SwitchGroup label="Theme:" options={PUBLIC_THEMES} value={theme} onChange={onTheme} numbered />
    <SwitchGroup label="Sign-in:" options={HERO_LAYOUTS} value={layout} onChange={onLayout} />
  </div>
);

export const PublicHeader = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const navLink = (path, label) => (
    <button
      onClick={() => navigate(path)}
      className={`relative py-2 text-base font-medium transition ${
        pathname === path ? 'text-white' : 'text-slate-300 hover:text-white'
      }`}
    >
      {label}
      {pathname === path && (
        <span className="absolute left-0 right-0 -bottom-1 h-0.5 rounded pub-accent-bg" />
      )}
    </button>
  );

  return (
    <nav className="sticky top-0 z-50 pub-page-glass backdrop-blur-md border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <button onClick={() => navigate('/')} className="flex items-center gap-3 text-left">
          <img src="/logo.png" alt="" className="w-11 h-11" />
          <span>
            <span className="block text-2xl font-bold text-white leading-none tracking-tight">LoadMemo</span>
            <span className="block mt-1 text-[10px] font-medium uppercase tracking-[0.3em] text-slate-300">
              One connected workspace
            </span>
          </span>
        </button>
        <div className="flex items-center gap-6 sm:gap-10">
          <div className="hidden md:flex items-center gap-10">
            {navLink('/features', 'Features')}
            {navLink('/solutions', 'Solutions')}
          </div>
          <button
            onClick={() => navigate('/contact')}
            className="px-5 py-2.5 rounded-lg border pub-accent-border pub-accent font-medium pub-hover-tint transition"
          >
            Contact Sales
          </button>
        </div>
      </div>
    </nav>
  );
};

export const PublicFooter = () => {
  const navigate = useNavigate();
  return (
    <footer className="pub-page border-t border-white/10 pb-16 sm:pb-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row gap-3 items-center justify-between text-sm text-slate-300">
        <p>&copy; {new Date().getFullYear()} LoadMemo. All rights reserved.</p>
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/privacy')} className="hover:text-white transition">Privacy Policy</button>
          <span className="text-white/20">|</span>
          <button onClick={() => navigate('/contact')} className="hover:text-white transition">Contact</button>
        </div>
      </div>
    </footer>
  );
};

// Small uppercase label with a leading rule, used above headlines.
export const Eyebrow = ({ children, center = false, className = '' }) => (
  <div className={`flex items-center gap-4 ${center ? 'justify-center' : ''} ${className}`}>
    <span className="w-12 h-0.5 pub-accent-bg" />
    <span className="text-xs sm:text-sm font-semibold uppercase tracking-[0.25em] pub-accent">{children}</span>
  </div>
);

// Standard page heading block for the inner public pages.
export const PageHero = ({ eyebrow, title, accent, children }) => (
  <section className="relative overflow-hidden pub-band pt-16 pb-12 sm:pt-20">
    <DecorLines />
    <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      {eyebrow && <Eyebrow center>{eyebrow}</Eyebrow>}
      <h1 className="mt-6 text-4xl sm:text-6xl font-extrabold tracking-tight leading-[1.05] text-white">
        {title}
        {accent && <span className="block pub-accent">{accent}</span>}
      </h1>
      {children && <div className="mt-5 text-lg sm:text-2xl text-slate-200">{children}</div>}
    </div>
  </section>
);

const FEATURES = [
  { icon: 'fa-building', title: 'Multi-Company Workspace', text: 'Manage companies and teams in one portal, with role-based access.' },
  { icon: 'fa-file-lines', title: 'Loads & Documents', text: 'Add loads manually or upload PDFs. Keep load details and documents together.' },
  { icon: 'fa-users', title: 'Driver Management', text: 'View drivers and assign loads within their company.' },
  { icon: 'fa-route', title: 'Route Planning', text: 'Plan routes and coordinate pickups and deliveries.' },
  { icon: 'fa-file-invoice-dollar', title: 'Accounting & Invoices', text: 'Track invoices, payments and outstanding balances.' },
  { icon: 'fa-chart-column', title: 'Dealer LiveLoad', text: 'Upload gate passes and compare carrier bids for vehicle transport.', href: '/features#liveload' },
];

// "Everything connected" six-card grid (design 3). Shared by Home and Features.
export const FeatureGridSection = ({ headingLevel: H = 'h2' }) => {
  const navigate = useNavigate();
  return (
    <section className="relative overflow-hidden pub-band py-16 sm:py-20">
      <DecorLines />
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Eyebrow center>The LoadMemo workspace</Eyebrow>
        <H className="mt-6 text-center text-4xl sm:text-6xl font-extrabold tracking-tight leading-[1.05] text-white">
          Everything connected.
          <span className="block pub-accent">More time to move.</span>
        </H>
        <p className="mt-5 text-center text-lg sm:text-2xl text-slate-200">
          Companies, loads, drivers, routes and finances — in one workspace.
        </p>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <button
              key={f.title}
              onClick={() => navigate(f.href || '/features')}
              className="pub-surface pub-line border text-left rounded-xl p-7 shadow-lg shadow-black/20 hover:-translate-y-1 hover:shadow-xl transition"
            >
              <div className="flex gap-6">
                <span className="pub-faint text-xs font-semibold pt-1">{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <i className={`fas ${f.icon} pub-icon text-4xl`} />
                  <h3 className="pub-text mt-4 text-xl font-bold">{f.title}</h3>
                  <p className="pub-muted mt-2 leading-relaxed">{f.text}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-10 text-center">
          <button
            onClick={() => navigate('/solutions')}
            className="inline-flex items-center gap-3 px-12 py-3.5 rounded-lg pub-btn text-lg font-semibold transition"
          >
            Explore solutions <i className="fas fa-arrow-right" />
          </button>
        </div>
      </div>
    </section>
  );
};

// Thin diagonal line decoration used on the dark sections.
export const DecorLines = () => (
  <svg className="pub-decor pointer-events-none absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 100" aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeWidth="0.12" vectorEffect="non-scaling-stroke">
      <polyline points="82,0 95,28 100,22" />
      <polyline points="0,74 14,100" />
      <polyline points="0,90 4,86 9,100" />
    </g>
  </svg>
);
